import {
  type AssetOperationExecutionTarget,
  type AssetOperationExecuteResponse,
} from '@fleet-bffs/api-contract';
import {
  AutoTimerEnabledSetting,
  EcoModeEnabledSetting,
  FacilityModeEnabledSetting,
  FairwayCuttingEnabledSetting,
  FrostSensorEnabledSetting,
  FrostSensorTempLimitSetting,
  HfsActionSetting,
  HfsSettingsActionEnum,
  MowerHouseEnabledSetting,
  ObstacleAvoidanceEnabledSetting,
  SpotCuttingEnabledSetting,
  MowerSmartFrostGuardSetting,
  MowerSmartRainGuardSetting,
} from 'fleet-data-model';
import {
  type HfsSettingsStatuses,
  SettingTypeEnum,
  type Update,
  type VisualSettingControl,
} from '../types';
import {
  mapFacilityModeEnabledExecutionTargets,
  mapObstacleAvoidanceEnabledExecutionTargets,
  mapFrostSensorTempLimitExecutionTargets,
  mapFrostSensorEnabledExecutionTargets,
  mapObstacleAvoidanceGroupExecutionTargets,
  mapFairwayCuttingExecutionTargets,
  mapWeatherTimerExecutionTargets,
  mapSpotCuttingExecutionTargets,
  mapMowerHouseExecutionTargets,
  mapEcoModeExecutionTargets,
  mapHeadlightsExecutionTargets,
  mapObstacleAvoidanceEnabledExecutionTarget,
  mapObstacleAvoidanceKeepRunningWithReducedPerformanceExecutionTarget,
  mapObstacleAvoidanceProfileExecutionTarget,
  mapWarningLightThemeTarget,
  mapWarningLightErrorIndicationEnabledTarget,
  mapWarningLightModeTarget,
  mapSmartRoutinesFrostGuardTarget,
  mapSmartRoutinesRainGuardTarget,
} from './hfs-actions-targets-mappers';

export const mapSettingExecutionTargets = (update: Update) => {
  switch (update.settingType) {
    case SettingTypeEnum.FrostSensorEnabled:
      return mapFrostSensorEnabledExecutionTargets(update);
    case SettingTypeEnum.FrostSensorTempLimit:
      return mapFrostSensorTempLimitExecutionTargets(update);
    case SettingTypeEnum.ObstacleAvoidanceEnabled:
      return mapObstacleAvoidanceEnabledExecutionTargets(update);
    case SettingTypeEnum.ObstacleAvoidanceGroup:
      return mapObstacleAvoidanceGroupExecutionTargets(update);
    case SettingTypeEnum.FacilityModeEnabled:
      return mapFacilityModeEnabledExecutionTargets(update);
    case SettingTypeEnum.FairwayCuttingEnabled:
      return mapFairwayCuttingExecutionTargets(update);
    case SettingTypeEnum.WeatherTimer:
      return mapWeatherTimerExecutionTargets(update);
    case SettingTypeEnum.SpotCutting:
      return mapSpotCuttingExecutionTargets(update);
    case SettingTypeEnum.MowerHouse:
      return mapMowerHouseExecutionTargets(update);
    case SettingTypeEnum.EcoMode:
      return mapEcoModeExecutionTargets(update);
    case SettingTypeEnum.Headlights:
      return mapHeadlightsExecutionTargets(update);
    case SettingTypeEnum.FrostGuardEnabled:
      return mapSmartRoutinesFrostGuardTarget(update);
    case SettingTypeEnum.RainGuardEnabled:
      return mapSmartRoutinesRainGuardTarget(update);
    default:
      return [];
  }
};

interface MappedSettingDataUpdate {
  settingDataUpdate: HfsActionSetting;
  hfsActionId: HfsSettingsActionEnum;
}

export const mapSettingDataUpdates = (update: Update): MappedSettingDataUpdate[] => {
  switch (update.settingType) {
    case SettingTypeEnum.FrostGuardEnabled:
      const frostGuardSetting = update.hfsAction?.settingData as
        | MowerSmartFrostGuardSetting
        | undefined;
      return [
        {
          settingDataUpdate: {
            value: {
              enabled: update.enabled,
              celsiusLimit: frostGuardSetting?.value?.celsiusLimit,
            },
          } as MowerSmartFrostGuardSetting,
          hfsActionId: HfsSettingsActionEnum.MOWER_SMART_FROST_GUARD,
        },
      ];
    case SettingTypeEnum.RainGuardEnabled:
      return [
        {
          settingDataUpdate: {
            value: update.enabled,
          } as MowerSmartRainGuardSetting,
          hfsActionId: HfsSettingsActionEnum.MOWER_SMART_RAIN_GUARD,
        },
      ];
    case SettingTypeEnum.FrostSensorEnabled:
      return [
        {
          settingDataUpdate: {
            value: update.enabled,
          } as FrostSensorEnabledSetting,
          hfsActionId: HfsSettingsActionEnum.FROST_SENSOR_ENABLED,
        },
      ];
    case SettingTypeEnum.FrostSensorTempLimit:
      return [
        {
          settingDataUpdate: {
            value: update.value,
          } as FrostSensorTempLimitSetting,
          hfsActionId: HfsSettingsActionEnum.FROST_SENSOR_TEMP_LIMIT,
        },
      ];
    case SettingTypeEnum.ObstacleAvoidanceEnabled:
      return [
        {
          settingDataUpdate: {
            value: update.enabled,
          } as ObstacleAvoidanceEnabledSetting,
          hfsActionId: HfsSettingsActionEnum.OBSTACLE_AVOIDANCE_ENABLED,
        },
      ];
    case SettingTypeEnum.ObstacleAvoidanceGroup:
      const enabledTarget = mapObstacleAvoidanceEnabledExecutionTarget(update);
      const keepRunningTarget =
        mapObstacleAvoidanceKeepRunningWithReducedPerformanceExecutionTarget(update);
      const profileTarget = mapObstacleAvoidanceProfileExecutionTarget(update);
      return enabledTarget === undefined ||
        keepRunningTarget === undefined ||
        profileTarget === undefined
        ? []
        : [
            {
              settingDataUpdate: {
                value: enabledTarget.payload.enabled,
              } as ObstacleAvoidanceEnabledSetting,
              hfsActionId: HfsSettingsActionEnum.OBSTACLE_AVOIDANCE_ENABLED,
            },
            {
              settingDataUpdate: {
                value: keepRunningTarget.payload.keepRunningWithReducedPerformance,
              } as HfsActionSetting,
              hfsActionId:
                HfsSettingsActionEnum.OBSTACLE_AVOIDANCE_VISION_KEEP_RUNNING_WITH_REDUCED_PERFORMANCE,
            },
            {
              settingDataUpdate: {
                value: profileTarget.payload.profile,
              } as HfsActionSetting,
              hfsActionId: HfsSettingsActionEnum.OBSTACLE_AVOIDANCE_VISION_PROFILE,
            },
          ];
    case SettingTypeEnum.FacilityModeEnabled:
      return [
        {
          settingDataUpdate: {
            value: update.enabled,
          } as FacilityModeEnabledSetting,
          hfsActionId: HfsSettingsActionEnum.FACILITY_MODE_ENABLED,
        },
      ];
    case SettingTypeEnum.FairwayCuttingEnabled:
      return [
        {
          settingDataUpdate: {
            value: update.enabled,
          } as FairwayCuttingEnabledSetting,
          hfsActionId: HfsSettingsActionEnum.FAIRWAY_CUTTING_ENABLED,
        },
      ];
    case SettingTypeEnum.WeatherTimer:
      return [
        {
          settingDataUpdate: {
            value: update.enabled,
          } as AutoTimerEnabledSetting,
          hfsActionId: HfsSettingsActionEnum.AUTO_TIMER_ENABLED,
        },
      ];
    case SettingTypeEnum.SpotCutting:
      return [
        {
          settingDataUpdate: {
            value: update.enabled,
          } as SpotCuttingEnabledSetting,
          hfsActionId: HfsSettingsActionEnum.SPOT_CUTTING_ENABLED,
        },
      ];
    case SettingTypeEnum.MowerHouse:
      return [
        {
          settingDataUpdate: {
            value: update.enabled,
          } as MowerHouseEnabledSetting,
          hfsActionId: HfsSettingsActionEnum.MOWER_HOUSE_ENABLED,
        },
      ];
    case SettingTypeEnum.EcoMode:
      return [
        {
          settingDataUpdate: {
            value: update.enabled,
          } as EcoModeEnabledSetting,
          hfsActionId: HfsSettingsActionEnum.ECO_MODE_ENABLED,
        },
      ];
    case SettingTypeEnum.Headlights:
      const theme = mapWarningLightThemeTarget(update);
      const errorIndication = mapWarningLightErrorIndicationEnabledTarget(update);
      const mode = mapWarningLightModeTarget(update);
      const list: MappedSettingDataUpdate[] = [];
      if (errorIndication !== undefined) {
        list.push({
          settingDataUpdate: {
            value: errorIndication.payload.errorIndicationEnabled,
          } as HfsActionSetting,
          hfsActionId: errorIndication.hfsActionId,
        });
      }
      if (theme !== undefined) {
        list.push({
          settingDataUpdate: {
            value: theme.payload.theme,
          } as HfsActionSetting,
          hfsActionId: theme.hfsActionId,
        });
      }
      if (mode !== undefined) {
        list.push({
          settingDataUpdate: {
            value: mode.payload.scheme,
          } as HfsActionSetting,
          hfsActionId: mode.hfsActionId,
        });
      }
      return list;
    default:
      return [];
  }
};

/*
 * Composes the list of HFS actions-based settings types, considering the feature toggles states.
 */
export const mapUpgradedSettingsTypesList = (statuses: HfsSettingsStatuses) => {
  return [
    SettingTypeEnum.ObstacleAvoidanceEnabled,
    SettingTypeEnum.ObstacleAvoidanceGroup,
    SettingTypeEnum.FrostSensorTempLimit,
    SettingTypeEnum.FrostGuardEnabled,
    SettingTypeEnum.RainGuardEnabled,
    SettingTypeEnum.FrostSensorEnabled,
    ...(statuses.facilityMode ? [SettingTypeEnum.FacilityModeEnabled] : []),
    SettingTypeEnum.FairwayCuttingEnabled,
    SettingTypeEnum.EcoMode,
    SettingTypeEnum.MowerHouse,
    SettingTypeEnum.SpotCutting,
    SettingTypeEnum.WeatherTimer,
    SettingTypeEnum.Headlights,
    SettingTypeEnum.HeadlightsMode,
    SettingTypeEnum.HeadlightsTheme,
    SettingTypeEnum.HeadlightsErrorIndication,
  ];
};

export const extractHfsActionsResponseOutputs = (
  responseItems: AssetOperationExecuteResponse[],
  targets: AssetOperationExecutionTarget[]
) => {
  return responseItems.filter((responseItem) =>
    targets.some((target) => target.hfsActionId === responseItem.hfsActionId)
  );
};

// The mower reported an invalid value, and the user has no change of their own pending or saving.
// A failed save drops the pending change, so the error shows again.
export const isShowingInvalidValue = (control: VisualSettingControl | undefined) =>
  Boolean(control?.invalidValue) && !control?.isLoading && !control?.isDirty;

export const getSettingsID = (settingType: string, parentId?: string, value?: string) => {
  return `${parentId ? parentId + '-' : ''}${settingType}${
    value ? '-' + value : ''
  }`.toLocaleLowerCase();
};
